/**
 * Pure builder-form helpers for automation rules (SE-5506).
 *
 * Two conversions, both React-free so they can be unit-tested directly:
 *   - {@link makeEmptyAutomationRuleForm} — a blank form for the given Kind;
 *   - {@link ruleToForm} — an existing rule fragment → editable form state,
 *     restoring `numericValue` as the exact decimal string it was stored as.
 *
 * Condition list keys are supplied by the caller (a monotonic counter in the
 * builder container) so these helpers stay pure — no `Math.random`/`Date.now`.
 * @module features/player-kanban/utils/automationRuleForm
 */

import {
  PipelineAutomationActionType,
  PipelineAutomationConditionType,
  type PipelineAutomationRuleFragmentFragment,
  PipelineAutomationTriggerType,
} from "@/generated/graphql";

import type {
  AutomationRuleConditionForm,
  AutomationRuleFormState,
  AutomationRuleKind,
} from "../types/automationRules";
import { classifyAutomationRuleKind } from "./automationRuleKind";
import { defaultComparatorForConditionType } from "./automationRuleOptions";

/** Default condition type used when a fresh condition row is added. */
export const DEFAULT_CONDITION_TYPE: PipelineAutomationConditionType =
  PipelineAutomationConditionType.Gpr;

/**
 * A blank condition row.
 *
 * The comparator is seeded from the condition's value kind (SE-6531) — the same
 * source the condition-type picker uses when re-kinding a row. Leaving it null
 * produced a row the Save gate could never accept while the comparator dropdown
 * displayed its placeholder as though a comparator had been chosen, which is
 * what disabled "Save rule" the moment any condition was added.
 * @param key - Caller-supplied stable list key.
 * @param conditionType - Optional initial condition type.
 * @returns An empty {@link AutomationRuleConditionForm}.
 */
export const makeEmptyCondition = (
  key: string,
  conditionType: PipelineAutomationConditionType = DEFAULT_CONDITION_TYPE
): AutomationRuleConditionForm => ({
  key,
  conditionType,
  comparator: defaultComparatorForConditionType(conditionType),
  numericValue: "",
  textValue: "",
  kanbanPhaseId: null,
});

/**
 * A blank builder form for the given Kind.
 *
 * Automation defaults to the first trigger/action (a card added → notify); Guard
 * defaults are the implicit CARD_ENTERED_STAGE + ADD_TO_REVIEW_QUEUE encoding,
 * though the mapper folds those on regardless of what is stored here.
 * @param kind - The Kind to seed.
 * @returns A blank {@link AutomationRuleFormState}.
 */
export const makeEmptyAutomationRuleForm = (
  kind: AutomationRuleKind = "automation"
): AutomationRuleFormState => ({
  kind,
  name: "",
  triggerType: PipelineAutomationTriggerType.CardAddedToPipeline,
  actionType: PipelineAutomationActionType.NotifyPipelineOwner,
  triggerKanbanPhaseId: null,
  targetKanbanPhaseId: null,
  kanbanTagId: null,
  requestedOutcome: null,
  reason: "",
  conditions: [],
});

/**
 * Convert an existing rule to editable form state.
 * @param rule - The rule fragment to edit.
 * @param makeKey - Stable-key factory for each condition row (index-based).
 * @returns The {@link AutomationRuleFormState}.
 */
export const ruleToForm = (
  rule: PipelineAutomationRuleFragmentFragment,
  makeKey: (index: number) => string
): AutomationRuleFormState => ({
  kind: classifyAutomationRuleKind(rule),
  name: rule.name,
  triggerType: rule.triggerType,
  actionType: rule.actionType,
  triggerKanbanPhaseId: rule.triggerKanbanPhaseId ?? null,
  targetKanbanPhaseId: rule.targetKanbanPhaseId ?? null,
  kanbanTagId: rule.kanbanTagId ?? null,
  requestedOutcome: rule.requestedOutcome ?? null,
  reason: rule.reason ?? "",
  conditions: rule.conditions.map((condition, index) => ({
    key: makeKey(index),
    conditionType: condition.conditionType,
    comparator: condition.comparator ?? null,
    // Preserve the exact decimal string the author saved.
    numericValue: condition.numericValue ?? "",
    textValue: condition.textValue ?? "",
    kanbanPhaseId: condition.kanbanPhaseId ?? null,
  })),
});
